// ABOUTME: TypeScript types for the Delivery Window configuration feature.
// ABOUTME: Covers day identifiers, DayChipToggle props, config/form types, and form component props.

/** Identifiers for days of the week plus holiday — used as toggle values */
export type DayIdentifier =
  | 'monday'
  | 'tuesday'
  | 'wednesday'
  | 'thursday'
  | 'friday'
  | 'saturday'
  | 'sunday'
  | 'holiday'

/** Canonical day ordering — single source of truth for the entire feature */
export const DAYS: DayIdentifier[] = [
  'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday', 'holiday',
]

/** Backend day code (3-letter uppercase) */
export type DayCode = 'MON' | 'TUE' | 'WED' | 'THU' | 'FRI' | 'SAT' | 'SUN' | 'HOL'

/** Maps backend day codes to frontend DayIdentifier */
export const DAY_CODE_TO_ID: Record<DayCode, DayIdentifier> = {
  MON: 'monday',
  TUE: 'tuesday',
  WED: 'wednesday',
  THU: 'thursday',
  FRI: 'friday',
  SAT: 'saturday',
  SUN: 'sunday',
  HOL: 'holiday',
}

/** Maps frontend DayIdentifier to backend day codes */
export const DAY_ID_TO_CODE: Record<DayIdentifier, DayCode> = {
  monday: 'MON',
  tuesday: 'TUE',
  wednesday: 'WED',
  thursday: 'THU',
  friday: 'FRI',
  saturday: 'SAT',
  sunday: 'SUN',
  holiday: 'HOL',
}

export interface DayChipToggleProps {
  /** Currently selected day identifiers */
  value: DayIdentifier[]
  /** Callback when selection changes — receives the new full array */
  onChange: (value: DayIdentifier[]) => void
  /** Days to render as chips (in any order — output is sorted canonically). Defaults to all DAYS. */
  days?: DayIdentifier[]
  /** Whether all chips are disabled */
  disabled?: boolean
  /** Individual days to disable (e.g., days not in businessDays) */
  disabledDays?: DayIdentifier[]
  /** Tooltip text shown on individually disabled day chips */
  disabledTooltip?: string
  /** Test identifier forwarded to the root element */
  'data-cy'?: string
}

import {OverrideValue, MetaResponse} from '.'

/** Business days data from the API */
export interface BusinessDaysDataDto {
  activeDays: DayCode[]
}

/** Time range from the API */
export interface TimeRangeDto {
  start: string
  end: string
}

/** Standard schedule from the API */
export interface StandardScheduleDto {
  standardDays: DayCode[]
  onlineDeliveryHours: TimeRangeDto
  defaultDeliveryHours: TimeRangeDto
}

/** Non-standard day schedule from the API */
export interface NonStandardDayDto {
  day: DayCode
  onlineDeliveryHours: TimeRangeDto
  defaultDeliveryHours: TimeRangeDto
}

/** Operating schedule from the API */
export interface OperatingScheduleDataDto {
  standard: StandardScheduleDto
  nonStandard: NonStandardDayDto[]
}

/** Full API response for GET /tuc-configs/delivery-windows */
export interface DeliveryWindowApiResponse {
  scopeId: string
  id: string
  timeZone: OverrideValue<string | null> | null
  businessDays: OverrideValue<BusinessDaysDataDto | null> | null
  operatingSchedule: OverrideValue<OperatingScheduleDataDto | null> | null
  minDeliveryWindow: OverrideValue<number | null> | null
  minFixDeliveryWindow: OverrideValue<number | null> | null
  stepInterval: OverrideValue<number | null> | null
  _meta: MetaResponse
}

export interface DeliveryWindowMeta {
  scopeId: string
  isInherited: boolean
  resolvedFrom: string | null
  createdAt: string
  updatedAt: string
  createdBy: string
  updatedBy: string
}

export interface DeliveryWindowOverrides {
  timeZone: boolean
  businessDays: boolean
  operatingSchedule: boolean
  minDeliveryWindow: boolean
  minFixDeliveryWindow: boolean
  stepInterval: boolean
}

export interface DeliveryWindowConfig {
  id: string | undefined
  timeZone: string
  businessDays: DayIdentifier[]
  slotManagementDays: DayIdentifier[]
  standardOperatingDays: DayIdentifier[]
  standardHours: Record<string, {start: string; end: string}>
  nonStandardHours: Record<string, Record<string, {start: string; end: string}>>
  deliveryConstraints: {
    minDeliveryWindow: number
    minFixDeliveryWindow: number
    stepInterval: number
  }
  overrides: DeliveryWindowOverrides
  parentValues: DeliveryWindowFormValues
  _meta: DeliveryWindowMeta
}

export type DeliveryWindowFormValues = Omit<DeliveryWindowConfig, 'id' | 'overrides' | '_meta' | 'parentValues'>

export interface DeliveryWindowFormProps {
  config: DeliveryWindowConfig
  isReadOnly: boolean
  isSlotManagementEnabled: boolean
  isCountryDefault: boolean
  onDirtyChange: (dirty: boolean) => void
  onValidChange: (valid: boolean) => void
  onValuesChange?: (values: DeliveryWindowFormValues) => void
  /** Backend day codes (e.g. "MON".."SUN","HOL"). Falls back to all DAYS when absent. */
  dayCodes?: string[]
}
